import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText } from '@mui/material';

import { IUnregisteredStepTwo } from '../unregistered-steps-types';
import { showModal } from '../../../../../store/slices/generalSlice';
import { FormLayout } from '../../../../../global-styles/layout';
import { theme } from '../../../../../global-styles/colors';
import { getErrorMessage } from '../../../../../constants/errorMessages';
import { Heading4Text, BodySmallText } from '../../../../../components/typography/typography';
import RadioCard from '../../../../../components/common/radio-card/radio-card';
import { FormInputTime } from '../../../../../components/common/forms/FormInputTime';
import { FormInputDate } from '../../../../../components/common/forms/FormInputDate';
import CardSubsection from '../../../../../components/card-subsection/card-subsection';

const Unregistered2: React.FC<IUnregisteredStepTwo> = ({ form, fromResume }) => {
  const { t } = useTranslation(['common', 'auth']);
  const shouldUpdate = useRef<boolean>(false);
  const dispatch = useDispatch();

  const [delayedLocal, setDelayedLocal] = useState(false);
  const [minTime, setMinTime] = useState<Date>(new Date());

  const {
    formState: { errors },
    control,
    trigger,
    watch,
    setValue,
    getValues,
  } = form;

  const delayed = watch('delayed', false);

  useEffect(() => {
    setValue('delayed', delayedLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedLocal]);

  // 1
  useEffect(() => {
    if ((errors.time || errors.date) && delayed === true) {
      dispatch(showModal({ name: 'date-picker-modal' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayed, errors.time, errors.date]);

  // 2
  useEffect(() => {
    if (fromResume && delayed === true) {
      dispatch(showModal({ name: 'date-picker-modal' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayed, fromResume]);

  // 3
  useEffect(() => {
    if (!shouldUpdate.current) {
      const [time, date] = getValues(['time', 'date']);
      const selectedDate = date ? new Date(date) : new Date();

      // Establecer hora mínima según la fecha seleccionada
      const today = new Date();
      const isToday = selectedDate.toDateString() === today.toDateString();
      let calculatedMinTime: Date;

      if (isToday) {
        // Si es hoy: 2 horas desde ahora
        calculatedMinTime = new Date(Date.now() + 2 * 60 * 60 * 1000);
        calculatedMinTime = new Date(Math.ceil(calculatedMinTime.getTime() / (5 * 60 * 1000)) * (5 * 60 * 1000));
      } else {
        // Si es fecha futura: medianoche (00:00)
        calculatedMinTime = new Date(selectedDate);
        calculatedMinTime.setHours(0, 0, 0, 0);
      }

      setMinTime(calculatedMinTime);

      setValue('date', selectedDate);
      // Solo establecer hora mínima si es hoy, de lo contrario mantener la hora actual o una hora por defecto
      if (isToday && calculatedMinTime) {
        setValue('time', calculatedMinTime);
      } else if (!time) {
        // Si no hay hora establecida y no es hoy, establecer una hora por defecto (ej: 09:00)
        const defaultTime = new Date(selectedDate);
        setValue('time', defaultTime);
      }

      if (delayed === true) {
        shouldUpdate.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayed]);

  // 4 - Actualizar minTime cuando cambie la fecha
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'date' && value.date) {
        const selectedDate = new Date(value.date);
        const today = new Date();
        const isToday = selectedDate.toDateString() === today.toDateString();
        let calculatedMinTime: Date;

        if (isToday) {
          // Si es hoy: 2 horas desde ahora
          calculatedMinTime = new Date(Date.now() + 2 * 60 * 60 * 1000);
        } else {
          // Si es fecha futura: medianoche (00:00)
          calculatedMinTime = new Date(selectedDate);
          calculatedMinTime.setHours(0, 0, 0, 0);
        }

        setMinTime(calculatedMinTime);

        // Solo actualizar la hora si cambiamos A hoy y la hora actual es menor que la mínima
        if (isToday && calculatedMinTime && value.time) {
          const currentTime = new Date(value.time);
          if (currentTime < calculatedMinTime) {
            setValue('time', calculatedMinTime);
          }
        }
        // Si cambiamos A una fecha futura, NO modificamos la hora - la dejamos como está
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  return (
    <>
      {/* ¿Cuándo desea realizar el servicio? */}
      <Heading4Text className="m-12px" weight={600}>
        {t('auth:unregistered.step-4.title-1')}
      </Heading4Text>
      <BodySmallText color={theme.type.secondary} className="m-24px">
        {' '}
        {t('auth:unregistered.step-4.text-1')}
      </BodySmallText>

      <FormControl className={'m-40px'} component="fieldset">
        <FormLayout>
          <RadioCard
            id={'delayed-1'}
            data-cy="delayed-now"
            selectedValue={delayedLocal == false}
            data={{
              value: 'false',
              title: t('auth:unregistered.step-4.option-1-title'),
              text: t('auth:unregistered.step-4.option-1-text'),
            }}
            onClick={() => {
              setDelayedLocal(false);
            }}
          />

          <RadioCard
            id={'delayed-2'}
            data-cy="delayed-later"
            selectedValue={delayedLocal == true}
            data={{
              value: 'true',
              title: t('auth:unregistered.step-4.option-2-title'),
              text: t('auth:unregistered.step-4.option-2-text'),
            }}
            onClick={() => {
              setDelayedLocal(true);
              // dispatch(showModal({ name: 'date-picker-modal' }));
            }}
          />
          {delayedLocal == true && (
            <CardSubsection>
              <FormLayout>
                <FormInputDate name="date" control={control} onChange={() => void trigger('time')} />
                <FormInputTime name="time" control={control} minTime={minTime} />
              </FormLayout>
            </CardSubsection>
          )}
        </FormLayout>
        <FormHelperText className="error-block" error>
          {getErrorMessage(t, errors.delayed?.type)}
        </FormHelperText>
      </FormControl>
    </>
  );
};

export default Unregistered2;
